import React, { useEffect, useState } from 'react';
import { flatMap } from 'lodash';
import moment from 'moment-timezone';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import CardLayout from '@magoosh/layouts/card';
import { DangerBadge, DefaultBadge, SuccessBadge } from '@magoosh/lib/badge';
import CollapsibleTaskLogs from '@magoosh/organisms/workflows/collapsible_task_logs';
import StatusBadge from '@magoosh/organisms/status_badge';
import { adminPaths } from 'config/path_helpers';

import { useQueries } from '@admin/app/queries';
import ContextualLink from '@admin/app/components/contextual_link';

import Actions from './components/actions';
import DraftPromptPreview from './components/draft_prompt_preview';
import { DraftPrompt } from './types';


const AIPromptRequestPage: React.FC<{}> = (props) => {
  const id = parseInt(useParams().id);
  const q = useQueries();

  const pageQuery = q.pageQuery(
    adminPaths.aiPromptRequest(id),
    {
      refetchInterval: (data) => {
        if (['processing', 'waiting'].indexOf(data.promptRequest.workflow.status) === -1) {
          return undefined;
        } else {
          return 2000;
        }
      }
    }
  );
  const page = pageQuery.data;
  const [groupedDraftPrompts, setGroupedDraftPrompts] = useState(
    page?.promptRequest?.groupedDraftPrompts
  );

  const allDraftPrompts = flatMap(groupedDraftPrompts, (group) => group.draftPrompts);
  const breadcrumbs = [
    { name: 'Admin', path: adminPaths.dashboard() },
    { name: 'Prompt Requests', path: adminPaths.aiPromptRequests() },
    { name: page?.promptRequest?.name }
  ];
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

  useEffect(() => {
    setGroupedDraftPrompts(page.promptRequest.groupedDraftPrompts);
  }, [page?.promptRequest?.groupedDraftPrompts]);

  const sourceContentPath = () => {
    if (page.promptRequest.sourceContent.type === 'Lesson') {
      return adminPaths.lesson(page.promptRequest.sourceContent.id);
    } else {
      return '#';
    }
  };

  const updateDraftPrompt = (updatedDraftPrompt: DraftPrompt) => {
    console.log(updatedDraftPrompt)
    const updatedGroupedDraftPrompts = groupedDraftPrompts.map((group) => {
      return {
        ...group,
        draftPrompts: group.draftPrompts.map((draftPrompt) => {
          if (draftPrompt.id === updatedDraftPrompt.id) {
            return updatedDraftPrompt;
          } else {
            return draftPrompt;
          }
        })
      };
    });

    setGroupedDraftPrompts(updatedGroupedDraftPrompts);
  };

  return (
    <>
      <Helmet>
        <title>Prompt Requests - {page?.promptRequest?.name || `${id}`}</title>
      </Helmet>

      <CardLayout breadcrumbs={breadcrumbs} linkComponent={ContextualLink} loading={!page}>
        {({ loading }) => {
          return (
            <div className="d-flex flex-column gap-m">
              <h1 className="text-body u-margin-A-n"><strong>{page.promptRequest.name}</strong></h1>

              {
                page.promptRequest.status === 'pending' && (
                  <div className="align-items-center d-flex flex-1">
                    <div className="d-flex gap-s">
                      <SuccessBadge>
                        {allDraftPrompts.filter((draftPrompt) => draftPrompt.status === 'accepted').length} Accepted
                      </SuccessBadge>

                      <DefaultBadge className="text-bold text-gray-light">
                        {allDraftPrompts.filter((draftPrompt) => draftPrompt.status === 'draft').length} Draft
                      </DefaultBadge>

                      <DangerBadge className="text-bold text-danger">
                        {allDraftPrompts.filter((draftPrompt) => draftPrompt.status === 'rejected').length} Rejected
                      </DangerBadge>
                    </div>

                    <div className="d-flex flex-1 justify-content-end">
                      <Actions
                        onComplete={() => pageQuery.refetch()}
                        promptRequest={page.promptRequest}
                      />
                    </div>
                  </div>
                )
              }

              <CardLayout.Card className="d-flex flex-column gap-s">
                <div>Status: <StatusBadge status={page.promptRequest.status} /></div>
                <div>Source Content: <ContextualLink href={sourceContentPath()}>{page.promptRequest.sourceContent.type}</ContextualLink></div>
                <div>Created At: {moment(page.promptRequest.createdAt * 1000).tz(timeZone).format('LLL')}</div>
                <div>Requested By: {page.promptRequest.requestedBy.name}</div>
              </CardLayout.Card>

              <div className="d-flex flex-column gap-s">
                <h3 className="u-margin-A-n"><strong>Workflow</strong></h3>

                <CardLayout.Card className="d-flex flex-column gap-s">
                  <div className="d-flex flex-column gap-s">
                    <div className="align-items-center d-flex gap-xs">
                      <StatusBadge status={page.promptRequest.workflow.status} />

                      <h4 className="u-margin-A-n">
                        <strong>
                          {page.promptRequest.workflow.name}
                        </strong>
                      </h4>
                    </div>

                    <hr className="u-margin-A-n" />

                    <CollapsibleTaskLogs tasks={page.promptRequest.workflow.tasks} />
                  </div>
                </CardLayout.Card>
              </div>

              <div className="d-flex flex-column gap-s">
                <h3 className="u-margin-A-n"><strong>Prompts</strong></h3>

                {
                  groupedDraftPrompts.map((group, idx) => (
                    <React.Fragment key={idx}>
                      {
                        group.group && (
                          <div className="d-flex flex-column gap-xs">
                            <hr className="u-margin-V-s" />
                            <h4 className="u-margin-A-n"><strong>{group.group.name}</strong></h4>
                            <div>{group.group.description}</div>
                            <hr className="u-margin-V-s" />
                          </div>
                        )
                      }

                      {
                        group.draftPrompts.map((draftPrompt) => (
                          <CardLayout.Card key={draftPrompt.id} className="d-flex flex-column gap-s">
                            <DraftPromptPreview
                              draftPrompt={draftPrompt}
                              isEditable={page.promptRequest.status === 'pending'}
                              onChange={(updatedDraftPrompt) => updateDraftPrompt(updatedDraftPrompt)}
                            />
                          </CardLayout.Card>
                        ))
                      }
                    </React.Fragment>
                  ))
                }
              </div>
            </div>
          );
        }}
      </CardLayout>
    </>
  );
};

export default AIPromptRequestPage;
